const TAG_BUCKETS = ['category_tags', 'purpose_tags', 'generic_tags'] as const;

export type ProductTagBucket = typeof TAG_BUCKETS[number];

export type NormalizedProductTags = Record<ProductTagBucket, string[]>;

const TAG_KEYWORDS: Record<string, string[]> = {
  '1-mukhi': ['1 mukhi', '1-mukhi', 'ek mukhi', '1mukhi'],
  '2-mukhi': ['2 mukhi', '2-mukhi', 'do mukhi', '2mukhi'],
  '3-mukhi': ['3 mukhi', '3-mukhi', 'teen mukhi', '3mukhi'],
  '4-mukhi': ['4 mukhi', '4-mukhi', 'chahar mukhi', '4mukhi'],
  '5-mukhi': ['5 mukhi', '5-mukhi', 'panch mukhi', '5mukhi'],
  '6-mukhi': ['6 mukhi', '6-mukhi', 'chhah mukhi', '6mukhi'],
  '7-mukhi': ['7 mukhi', '7-mukhi', 'saat mukhi', '7mukhi'],
  '8-mukhi': ['8 mukhi', '8-mukhi', 'aath mukhi', '8mukhi'],
  '9-mukhi': ['9 mukhi', '9-mukhi', 'nau mukhi', '9mukhi'],
  '10-mukhi': ['10 mukhi', '10-mukhi', 'das mukhi', '10mukhi'],
  'nepali-rudraksha': ['nepali'],
  'indonesian-rudraksha': ['indonesian', 'java'],
  'rudraksha-mala': ['mala'],
  'rudraksha-bracelet': ['bracelet', 'kada'],
  'evil-eye': ['evil eye', 'evil-eye', 'aghori', 'gomti rudraksha tree'],
};

const TAG_EXCLUDED_KEYWORDS: Record<string, string[]> = {
  'evil-eye': ['tiger eye', 'tiger-eye'],
};

const emptyTags = (): NormalizedProductTags => ({
  category_tags: [],
  purpose_tags: [],
  generic_tags: [],
});

const toTagArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .filter((tag): tag is string => typeof tag === 'string')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((tag) => tag.trim())
      .filter(Boolean);
  }

  return [];
};

export const normalizeProductTags = (tags: unknown): NormalizedProductTags => {
  const normalized = emptyTags();

  if (Array.isArray(tags) || typeof tags === 'string') {
    normalized.generic_tags = toTagArray(tags);
    return normalized;
  }

  if (!tags || typeof tags !== 'object') {
    return normalized;
  }

  const tagsObject = tags as Record<string, unknown>;
  TAG_BUCKETS.forEach((bucket) => {
    normalized[bucket] = toTagArray(tagsObject[bucket]);
  });

  return normalized;
};

export const productHasTag = (tags: unknown, targetTag: string, name?: string, slug?: string): boolean => {
  const normalizedTarget = targetTag.trim().toLowerCase();
  if (!normalizedTarget) return false;

  const normalizedTags = normalizeProductTags(tags);

  const lowerName = (name || '').toLowerCase();
  const lowerSlug = (slug || '').toLowerCase();
  const excludedKeywords = TAG_EXCLUDED_KEYWORDS[normalizedTarget] || [];
  if (excludedKeywords.some(kw => lowerName.includes(kw) || lowerSlug.includes(kw))) return false;

  const tagMatch = TAG_BUCKETS.some((bucket) =>
    normalizedTags[bucket].some((tag) => tag.toLowerCase() === normalizedTarget)
  );

  if (tagMatch) return true;

  const tagKeywords = TAG_KEYWORDS[normalizedTarget];
  if (tagKeywords && (name || slug)) {
    return tagKeywords.some(kw => lowerName.includes(kw) || lowerSlug.includes(kw));
  }

  return false;
};

export const buildSupabaseTagOrFilter = (tag: string): string => {
  const normalizedTag = tag.trim().toLowerCase();

  if (!normalizedTag) {
    return 'internal_sku.is.null';
  }

  // Product tags were historically imported with mixed casing. JSONB
  // containment is case-sensitive, while productHasTag is not, so query the
  // same case variants that the client-side matcher accepts.
  const tagVariants = [...new Set([
    normalizedTag,
    normalizedTag.replace(/(^|-)([a-z])/g, (_, separator: string, letter: string) => `${separator}${letter.toUpperCase()}`),
    normalizedTag.toUpperCase(),
  ])];

  const jsonbFilters = tagVariants.flatMap((tagVariant) => [
    ...TAG_BUCKETS.map((bucket) =>
      `tags.cs.${JSON.stringify({ [bucket]: [tagVariant] })}`
    ),
    ...TAG_BUCKETS.map((bucket) =>
      `tags.cs.${JSON.stringify({ [bucket]: tagVariant })}`
    ),
    `tags.cs.${JSON.stringify([tagVariant])}`,
  ]);

  const tagKeywords = TAG_KEYWORDS[normalizedTag];
  if (tagKeywords) {
    const ilikeFilters = tagKeywords.flatMap(kw => [
      `original_name.ilike.%${kw}%`,
      `slug.ilike.%${kw}%`,
    ]);
    return [...jsonbFilters, ...ilikeFilters].join(',');
  }

  return jsonbFilters.join(',');
};
